import React, { useState, useEffect } from "react";
import { apiFetch } from "../utils/api";
import { toast } from "../components/Toast";

const RoutesPage = () => {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Route create/edit form
  const [form, setForm] = useState({ route_id: null, route_name: "", route_code: "", description: "", vehicle_code: "" });
  const [stops, setStops] = useState([{ stop_name: "", latitude: "", longitude: "", stop_sequence: 1 }]);

  // Import flow state
  const [importFile, setImportFile] = useState(null);           // parsed JSON data
  const [importFileName, setImportFileName] = useState("");     // display name
  const [importVehicleCode, setImportVehicleCode] = useState(""); // selected bus code
  const [unassignedCodes, setUnassignedCodes] = useState([]);   // available bus codes
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");

  const ITEMS_PER_PAGE = 10;

  // Filter routes by code, name, or bus code
  const filteredRoutes = routes.filter(route => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const routeCode = (route.route_code || "").toLowerCase();
    const routeName = (route.route_name || "").toLowerCase();
    const busCode = (route.vehicle_code || "").toLowerCase();
    return routeCode.includes(query) || routeName.includes(query) || busCode.includes(query);
  });

  const totalPages = Math.ceil(filteredRoutes.length / ITEMS_PER_PAGE);
  const paginatedRoutes = filteredRoutes.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const handlePrevPage = () => setCurrentPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setCurrentPage((p) => Math.min(totalPages, p + 1));

  const loadRoutes = async () => {
    setLoading(true);
    try {
      const res = await apiFetch("/routes");
      setRoutes(res.data || []);
    } catch { toast.error("Failed loading routes list"); }
    finally { setLoading(false); }
  };

  const loadUnassignedCodes = async () => {
    try {
      const res = await apiFetch("/vehicles/unassigned-codes");
      setUnassignedCodes(res.data || []);
    } catch { setUnassignedCodes([]); }
  };

  useEffect(() => { loadRoutes(); }, []);

  // ── Import JSON flow ──────────────────────────────────────────────────────

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith(".json")) {
      toast.error("Please select a valid .json file");
      e.target.value = "";
      return;
    }

    try {
      const fileContent = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (ev) => resolve(ev.target?.result);
        reader.onerror = reject;
        reader.readAsText(file);
      });

      const jsonData = JSON.parse(fileContent);

      if (!jsonData.route_name || !jsonData.route_code || !Array.isArray(jsonData.stops)) {
        toast.error("Invalid JSON structure. Required: route_name, route_code, stops[]");
        e.target.value = "";
        return;
      }
      if (jsonData.stops.length === 0) {
        toast.error("JSON must contain at least one stop");
        e.target.value = "";
        return;
      }

      setImportFile(jsonData);
      setImportFileName(file.name);
      setImportVehicleCode("");
      // Fetch unassigned bus codes for the dropdown
      await loadUnassignedCodes();
    } catch (err) {
      if (err instanceof SyntaxError) toast.error("Invalid JSON format.");
      else toast.error(err.message || "Failed to read file");
      e.target.value = "";
    }
  };

  const handleImportSubmit = async () => {
    if (!importFile) return;
    setSubmitting(true);
    try {
      const normalizedStops = importFile.stops.map((stop) => ({
        stop_name: stop.stop_name,
        latitude: parseFloat(stop.latitude),
        longitude: parseFloat(stop.longitude),
        stop_sequence: stop.sequence || stop.stop_sequence || 1,
      }));

      const res = await apiFetch("/routes/import", {
        method: "POST",
        body: JSON.stringify({
          route_name: importFile.route_name,
          route_code: importFile.route_code,
          description: importFile.description || "",
          vehicle_code: importVehicleCode || null,
          stops: normalizedStops,
        }),
      });

      if (res && res.warning) {
        toast.error(res.warning);
      }
      toast.success(`Route "${importFile.route_name}" imported successfully!`);
      setImportFile(null);
      setImportFileName("");
      setImportVehicleCode("");
      loadRoutes();
    } catch (err) {
      toast.error(err.message || "Failed to import route");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelImport = () => {
    setImportFile(null);
    setImportFileName("");
    setImportVehicleCode("");
  };

  // ── Route create/edit form ────────────────────────────────────────────────

  const handleFormChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleStopChange = (index, field, value) => {
    const updated = [...stops];
    updated[index][field] = value;
    setStops(updated);
  };

  const addStop = () => setStops([...stops, { stop_name: "", latitude: "", longitude: "", stop_sequence: stops.length + 1 }]);

  const removeStop = (index) => {
    setStops(stops.filter((_, i) => i !== index).map((s, i) => ({ ...s, stop_sequence: i + 1 })));
  };

  const handleEditOpen = async (routeId) => {
    try {
      const res = await apiFetch(`/routes/${routeId}`);
      const data = res.data;
      setForm({
        route_id: routeId,
        route_name: data.route_name,
        route_code: data.route_code,
        description: data.description || "",
        vehicle_code: data.vehicle_code || "",
      });
      setStops(data.stops?.length > 0 ? data.stops : [{ stop_name: "", latitude: "", longitude: "", stop_sequence: 1 }]);
      // Load unassigned codes + include the currently assigned one so it shows in dropdown
      await loadUnassignedCodes();
      setIsModalOpen(true);
    } catch { toast.error("Failed to fetch route definition."); }
  };

  const handleAddOpen = async () => {
    setForm({ route_id: null, route_name: "", route_code: "", description: "", vehicle_code: "" });
    setStops([{ stop_name: "", latitude: "", longitude: "", stop_sequence: 1 }]);
    await loadUnassignedCodes();
    setIsModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        vehicle_code: form.vehicle_code || null,
        stops: stops.map((s) => ({ ...s, latitude: parseFloat(s.latitude), longitude: parseFloat(s.longitude) })),
      };
      if (form.route_id) {
        const res = await apiFetch(`/routes/${form.route_id}`, { method: "PUT", body: JSON.stringify(payload) });
        if (res && res.warning) toast.error(res.warning);
        toast.success("Route updated.");
      } else {
        const res = await apiFetch("/routes", { method: "POST", body: JSON.stringify(payload) });
        if (res && res.warning) toast.error(res.warning);
        toast.success("Route created.");
      }
      setIsModalOpen(false);
      loadRoutes();
    } catch (e) {
      toast.error(e.message || "Failed saving route");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (routeId) => {
    if (!window.confirm("Delete this route?")) return;
    try {
      await apiFetch(`/routes/${routeId}`, { method: "DELETE" });
      toast.success("Deleted successfully");
      loadRoutes();
    } catch (e) {
      // Show specific error for routes with active trips
      if (e.status === 409) {
        toast.error(e.message || "This route has active trips. Please delete all trips for this route before deleting the route.");
      } else {
        toast.error(e.message || "Failed to delete route");
      }
    }
  };

  // When editing, the currently assigned code should also appear in the dropdown
  const dropdownCodes = form.vehicle_code && !unassignedCodes.find((c) => c.vehicle_code === form.vehicle_code)
    ? [{ vehicle_code: form.vehicle_code, registration_no: "(current)" }, ...unassignedCodes]
    : unassignedCodes;

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 className="page-title text-gradient" style={{ margin: 0 }}>Route Configurations</h1>
        <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
          <input
            type="text"
            placeholder="Search by code, name or bus code..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="form-control"
            style={{ width: "280px", height: "40px" }}
          />
          <label className="btn-brown" style={{ height: "40px", display: "inline-flex", alignItems: "center" }}>
            📁 Import JSON
            <input type="file" accept=".json" onChange={handleFileSelect} disabled={submitting} style={{ display: "none" }} />
          </label>
          <button className="btn-primary" onClick={handleAddOpen} style={{ height: "40px" }}>+ Add Route</button>
        </div>
      </div>

      {/* Import confirmation panel — shown after file is selected */}
      {importFile && (
        <div className="glass-panel" style={{ padding: "20px", marginBottom: "24px", border: "1px solid var(--border-dark)" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "16px" }}>
            <div>
              <div style={{ fontWeight: 700, fontSize: "1rem" }}>Import: {importFile.route_name}</div>
              <div style={{ fontSize: "0.85rem", color: "var(--text-muted)", marginTop: "2px" }}>
                {importFileName} · {importFile.stops?.length || 0} stops · Code: {importFile.route_code}
              </div>
            </div>
            <button className="btn-ghost-danger" onClick={handleCancelImport} style={{ fontSize: "1.2rem" }}>×</button>
          </div>

          <div style={{ display: "flex", gap: "16px", alignItems: "flex-end" }}>
            <div className="form-group" style={{ margin: 0, flex: 1 }}>
              <label className="form-label">Assign Bus Code (optional)</label>
              <select
                className="form-control"
                value={importVehicleCode}
                onChange={(e) => setImportVehicleCode(e.target.value)}
              >
                <option value="">— No Bus Code —</option>
                {unassignedCodes.map((c) => (
                  <option key={c.vehicle_code} value={c.vehicle_code}>
                    {c.vehicle_code} ({c.registration_no})
                  </option>
                ))}
              </select>
              {unassignedCodes.length === 0 && (
                <small style={{ color: "var(--text-muted)", marginTop: "4px", display: "block" }}>
                  No unassigned bus codes available. Add vehicles with bus codes first.
                </small>
              )}
            </div>
            <button
              className="btn-primary"
              onClick={handleImportSubmit}
              disabled={submitting}
              style={{ height: "48px", minWidth: "120px" }}
            >
              {submitting ? "Importing..." : "Confirm Import"}
            </button>
          </div>
        </div>
      )}

      <div className="data-table-container glass-panel" style={{ display: 'flex', flexDirection: 'column' }}>
        <table className="data-table" style={{ flex: 1, fontSize: '14px' }}>
          <thead>
            <tr style={{ height: '48px' }}>
              <th style={{ padding: '10px 16px' }}>Code</th>
              <th style={{ padding: '10px 16px' }}>Route Name</th>
              <th style={{ padding: '10px 16px' }}>Bus Code</th>
              <th style={{ padding: '10px 16px' }}>Total Stops</th>
              <th style={{ padding: '10px 16px' }}>Description</th>
              <th style={{ padding: '10px 16px' }}>Status</th>
              <th style={{ padding: '10px 16px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr style={{ height: '48px' }}><td colSpan="7" style={{ textAlign: "center", padding: '10px 16px' }}>Loading routes...</td></tr>
            ) : routes.length === 0 ? (
              <tr style={{ height: '48px' }}><td colSpan="7" style={{ textAlign: "center", padding: '10px 16px' }}>No routes found.</td></tr>
            ) : paginatedRoutes.map((r) => (
              <tr key={r.route_id} style={{ height: '48px' }}>
                <td style={{ fontWeight: "800", padding: '10px 16px' }}>{r.route_code}</td>
                <td style={{ padding: '10px 16px' }}>{r.route_name}</td>
                <td style={{ padding: '10px 16px' }}>
                  {r.vehicle_code
                    ? <span className="badge badge-active">{r.vehicle_code}</span>
                    : <span style={{ color: "var(--text-muted)", fontStyle: "italic" }}>—</span>}
                </td>
                <td style={{ padding: '10px 16px' }}>{r.stop_count} stops</td>
                <td style={{ color: "var(--text-muted)", fontSize: "0.9rem", padding: '10px 16px' }}>{r.description || "-"}</td>
                <td style={{ padding: '10px 16px' }}>
                  <span className={`badge ${r.is_active ? "badge-active" : "badge-inactive"}`}>
                    {r.is_active ? "Active" : "Inactive"}
                  </span>
                </td>
                <td style={{ padding: '10px 16px' }}>
                  <div className="table-actions">
                    <button className="btn-secondary" onClick={() => handleEditOpen(r.route_id)}>Edit</button>
                    <button className="btn-danger" onClick={() => handleDelete(r.route_id)}>Delete</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination */}
        {routes.length > 0 && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            padding: '16px',
            borderTop: '1px solid var(--border-color)'
          }}>
            <button
              className="btn-secondary"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
              style={{ opacity: currentPage === 1 ? 0.5 : 1 }}
            >
              ← Previous
            </button>
            <span style={{ color: 'var(--text-muted)' }}>
              Page {currentPage} of {totalPages}
            </span>
            <button
              className="btn-secondary"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
              style={{ opacity: currentPage === totalPages ? 0.5 : 1 }}
            >
              Next →
            </button>
          </div>
        )}
      </div>

      {/* Create / Edit modal */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content large">
            <div className="modal-header">
              <h2 className="modal-title">{form.route_id ? "Edit Route" : "New Route"}</h2>
              <button className="modal-close" type="button" onClick={() => setIsModalOpen(false)}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Route Code</label>
                  <input type="text" className="form-control" name="route_code" value={form.route_code} onChange={handleFormChange} required placeholder="R-01" />
                </div>
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Route Name</label>
                  <input type="text" className="form-control" name="route_name" value={form.route_name} onChange={handleFormChange} required placeholder="North City Loop" />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group" style={{ flex: 2 }}>
                  <label className="form-label">Description (Optional)</label>
                  <input type="text" className="form-control" name="description" value={form.description} onChange={handleFormChange} />
                </div>
                <div className="form-group" style={{ flex: 1 }}>
                  <label className="form-label">Bus Code</label>
                  <select
                    className="form-control"
                    name="vehicle_code"
                    value={form.vehicle_code}
                    onChange={handleFormChange}
                  >
                    <option value="">— Unassigned —</option>
                    {dropdownCodes.map((c) => (
                      <option key={c.vehicle_code} value={c.vehicle_code}>
                        {c.vehicle_code} ({c.registration_no})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div style={{ marginTop: "24px", marginBottom: "16px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h3 style={{ fontSize: "1.1rem" }}>Stops</h3>
                <button type="button" className="btn-secondary" onClick={addStop}>+ Append Stop</button>
              </div>

              <div style={{ maxHeight: "300px", overflowY: "auto", paddingRight: "8px" }}>
                {stops.map((s, index) => (
                  <div key={index} style={{ background: "var(--bg-alt)", padding: "14px", borderRadius: "8px", marginBottom: "10px", border: "1px solid var(--border-color)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                      <span style={{ fontWeight: 600, fontSize: "0.85rem", color: "var(--text-muted)" }}>Stop {s.stop_sequence}</span>
                      {stops.length > 1 && (
                        <button type="button" className="btn-ghost-danger" onClick={() => removeStop(index)}>Remove</button>
                      )}
                    </div>
                    <div className="form-group" style={{ marginBottom: "10px" }}>
                      <input type="text" className="form-control" placeholder="Stop name" value={s.stop_name} onChange={(e) => handleStopChange(index, "stop_name", e.target.value)} required />
                    </div>
                    <div className="form-row" style={{ marginBottom: 0 }}>
                      <div className="form-group">
                        <input type="number" step="any" className="form-control" placeholder="Latitude" value={s.latitude} onChange={(e) => handleStopChange(index, "latitude", e.target.value)} required />
                      </div>
                      <div className="form-group">
                        <input type="number" step="any" className="form-control" placeholder="Longitude" value={s.longitude} onChange={(e) => handleStopChange(index, "longitude", e.target.value)} required />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: "20px" }}>
                <button type="submit" className="btn-primary btn-block" disabled={submitting}>
                  {submitting ? "Saving..." : (form.route_id ? "Save Changes" : "Create Route")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default RoutesPage;
